import { ArrowRight } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { CampoPercentual } from '@/componentes/campo-percentual';
import { Celula } from '@/componentes/celula';
import { RodapeFunil } from '@/componentes/rodape-funil';
import { Button } from '@/componentes/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/componentes/ui/card';
import {
  mensagemDoErro,
  useEstadoCnpj,
  useEu,
  useIntegrador,
  useSalvarIntegrador,
  type Integrador,
} from '@/dados';
import { Pagina } from '@/layout/pagina';
import { cn } from '@/lib/utils';

import { CnpjOcupado } from './cnpj-ocupado';
import {
  daLinha,
  META_PADRAO,
  retornoSeguro,
  validar,
  type Erros,
  type Formulario,
} from './formulario';
import { Identificacao } from './identificacao';

/* Empresa — a primeira etapa do funil: quem é a empresa e qual margem ela
 * persegue.
 *
 * Duas regras herdadas da versão anterior, e as duas custaram suporte:
 *
 *  1. Os campos nascem DESABILITADOS e só abrem depois que o integrador é
 *     carregado. Digitar antes disso seria sobrescrito quando a resposta
 *     chegasse, e a pessoa perderia o que escreveu sem entender por quê.
 *  2. Falha ao salvar SEGURA o usuário nesta tela. Seguir adiante com o dado
 *     perdido é pior que a fricção de tentar de novo.
 */

export function Empresa() {
  const integrador = useIntegrador();
  const eu = useEu();
  const salvar = useSalvarIntegrador();
  const navegar = useNavigate();
  const [busca] = useSearchParams();

  // Quem chega pelo "Editar empresa" de outra tela volta para lá; quem está no
  // funil segue para os custos. Endereço externo é descartado: um returnTo
  // aberto vira redirecionamento para fora do produto.
  const retorno = retornoSeguro(busca.get('returnTo'));
  const destino = retorno ?? '/custos';

  const [form, setForm] = React.useState<Formulario | null>(null);
  const [erros, setErros] = React.useState<Erros>({});

  // Preenche UMA vez, quando a linha do banco chega. Depois disso o formulário
  // é do usuário: nem refetch nem invalidação reescrevem o que ele digitou.
  const carregou = !integrador.isPending && !eu.isPending;
  React.useEffect(() => {
    if (form !== null || !carregou) return;
    setForm(daLinha(integrador.data ?? null, eu.data?.email ?? null));
  }, [form, carregou, integrador.data, eu.data]);

  const podeEditar = form !== null && !integrador.isError;
  const salvando = salvar.isPending;
  const jaCadastrada = Boolean(integrador.data?.nome_empresa);

  /* Uma empresa, um cadastro. A pergunta sai assim que o CNPJ fica completo,
     antes de a pessoa preencher o resto à toa. Quem manda é a API: isto aqui
     evita o trabalho perdido, não substitui a recusa na gravação. */
  const estadoCnpj = useEstadoCnpj(form?.cnpj);
  const ocupado =
    estadoCnpj.data?.estado === 'exige_login' || estadoCnpj.data?.estado === 'exige_cadastro'
      ? estadoCnpj.data.estado
      : null;

  // A caixa abre sozinha quando o CNPJ se revela ocupado, e uma vez só: quem
  // fechar para conferir o número não a vê reaparecer a cada tecla.
  const [pedindoAcesso, setPedindoAcesso] = React.useState(false);
  const jaAbriu = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!ocupado || form === null || jaAbriu.current === form.cnpj) return;
    jaAbriu.current = form.cnpj;
    setPedindoAcesso(true);
  }, [ocupado, form]);

  function alterar<C extends keyof Formulario>(campo: C, valor: string) {
    setForm((atual) => (atual === null ? atual : { ...atual, [campo]: valor }));
    // O erro some assim que a pessoa mexe no campo: manter a mensagem embaixo
    // de um campo que já foi corrigido é ruído.
    setErros((atual) => (atual[campo] === undefined ? atual : { ...atual, [campo]: undefined }));
  }

  async function aoEnviar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (form === null || salvando) return;

    const achados = validar(form);
    setErros(achados);
    if (Object.keys(achados).length > 0) return;
    // CNPJ de outra empresa: a barreira acima é o caminho, não este envio.
    if (ocupado) return;

    const meta = parseFloat(form.lucro_alvo);
    const patch: Partial<Integrador> = {
      nome_empresa: form.nome_empresa.trim(),
      cnpj: form.cnpj.trim() === '' ? null : form.cnpj.trim(),
      regime_tributario: form.regime_tributario,
      email: form.email.trim(),
      whatsapp: form.whatsapp.trim(),
      lucro_alvo: Number.isFinite(meta) ? meta : META_PADRAO,
    };

    try {
      await salvar.mutateAsync(patch);
      navegar(destino);
    } catch {
      // A mensagem já está em `salvar.error` e é desenhada abaixo do rodapé.
      // O importante aqui é NÃO navegar.
    }
  }

  return (
    <Pagina
      titulo="Empresa"
      descricao="Quem é a empresa e qual margem de preço desejada."
      etapa="empresa"
      semIdentidade
      /* Quem já cadastrou a empresa quase sempre está de passagem, indo
         corrigir custo lá na frente. A saída SEM gravar mora no cabeçalho,
         que é onde ficam as ações da tela, e não numa faixa própria: a faixa
         gastava uma banda inteira para repetir o nome da empresa que já
         estava escrito no primeiro campo, logo abaixo dela. O rótulo diz
         "sem alterar" para não se confundir com o "Continuar" do rodapé, que
         grava o formulário. */
      acoes={
        jaCadastrada && !retorno ? (
          <Button type="button" variant="outline" onClick={() => navegar(destino)}>
            Continuar sem alterar
          </Button>
        ) : undefined
      }
    >
      {/* UMA coluna, dois blocos empilhados, e os campos em metades dentro
          de cada cartão. Lado a lado (cadastro à esquerda, meta à direita) a
          meta era um cartão de um campo só ao lado de um cartão de cinco: a
          linha nunca fechava e sobrava vazio no meio da tela. Empilhados, o
          cartão inteiro é a régua e todo controle tem metade dela, que é o
          mesmo eixo das outras etapas do funil. */}
      <form onSubmit={aoEnviar} className="favo" noValidate>
        {integrador.isError ? (
          <Aviso titulo="Não foi possível carregar os dados da empresa">
            <p>{mensagemDoErro(integrador.error)}</p>
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void integrador.refetch()}
              >
                Tentar de novo
              </Button>
            </div>
          </Aviso>
        ) : null}

        {ocupado && form ? (
          <>
            {/* Com a caixa fechada, a tela precisa continuar dizendo por que o
                botão de continuar não grava, e oferecer o caminho de volta. */}
            <Aviso titulo="Este CNPJ já tem cadastro">
              <p>
                {ocupado === 'exige_login'
                  ? 'Entre com o e-mail usado no cadastro para continuar de onde a empresa parou.'
                  : 'O cadastro ainda não tem conta. Crie a senha com o e-mail do primeiro preenchimento.'}
              </p>
              <div>
                <Button type="button" variant="outline" size="sm" onClick={() => setPedindoAcesso(true)}>
                  {ocupado === 'exige_login' ? 'Entrar nesta empresa' : 'Assumir este cadastro'}
                </Button>
              </div>
            </Aviso>

            <CnpjOcupado
              cnpj={form.cnpj}
              estado={ocupado}
              aberto={pedindoAcesso}
              aoFechar={() => setPedindoAcesso(false)}
              /* A sessão passou a ser a dona do CNPJ. Zerar o formulário
                 evita que a tela mostre por um instante o que a pessoa tinha
                 digitado antes de provar quem era; a navegação leva para o
                 trabalho, que é o que ela veio fazer. */
              aoEntrar={() => {
                setPedindoAcesso(false);
                setForm(null);
                navegar('/');
              }}
            />
          </>
        ) : null}

        <Identificacao
          form={form}
          erros={erros}
          podeEditar={podeEditar}
          aoAlterar={alterar}
        />


        <Card>
          <CardHeader>
            <CardTitle>Meta de margem</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 @campos:grid-cols-2 @campos:gap-x-8">
            {/* A explicação do conceito foi para a tooltip do rótulo. Impressa
                no cartão, ela era três linhas de texto para um campo de dois
                dígitos: o bloco lia como aviso, não como pergunta. */}
            <CampoPercentual
              rotulo="Margem líquida alvo"
              dica="O preço sugerido é o menor preço que entrega esta margem depois de custos, comissão, tributo e rateio da despesa fixa. Se a margem de hoje já for maior, a ferramenta mostra a folga em vez de sugerir preço menor."
              ajuda="Padrão de 12% quando não informada."
              erro={erros.lucro_alvo}
              valor={form?.lucro_alvo ?? ''}
              aoMudar={(valor) => alterar('lucro_alvo', valor)}
              disabled={!podeEditar}
              max={100}
              placeholder="12"
            />
          </CardContent>
        </Card>

        {salvar.isError ? (
          <Aviso titulo="Não foi possível salvar">
            <p>{mensagemDoErro(salvar.error)}</p>
            <p>Corrija o que for necessário e envie de novo. Nada foi gravado.</p>
          </Aviso>
        ) : null}

        {/* A primeira etapa não tem para onde voltar dentro do funil, então o
            lado esquerdo da barra só existe quando a tela foi aberta a partir
            de outra: aí ele sai SEM salvar, de volta à origem. */}
        <RodapeFunil
          etapa="empresa"
          {...(retorno ? { voltar: retorno, rotuloVoltar: 'Voltar sem salvar' } : {})}
        >
          <Button type="submit" disabled={!podeEditar || salvando || ocupado !== null}>
            {salvando ? 'Salvando…' : retorno ? 'Salvar e voltar' : 'Continuar'}
            {salvando || retorno ? null : <ArrowRight />}
          </Button>
        </RodapeFunil>
      </form>
    </Pagina>
  );
}

/* ── Peças locais ─────────────────────────────────────────────────────────── */

function Aviso({
  titulo,
  className,
  children,
}: {
  titulo: string;
  className?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <Celula
      campo="telha"
      respiro="justo"
      role="alert"
      className={cn('text-negativo grid gap-2 text-apoio', className)}
    >
      <strong className="font-semibold">{titulo}</strong>
      {children}
    </Celula>
  );
}

